import React, { useState } from 'react';
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
    DaCheckboxField,
    DaTextField,
    Form,
    ButtonBar,
    DaButton,
    HTMLComment,
} from "../form";
import { IMessagePanelProps, MessageBox, MsgBoxType } from "../dialog";
import { ISignatureDialogProps } from "./types";
import { pafApplicationSignatureFields } from "../../../_appData";
import { IPafApplicationSignature } from "../../../_api";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { boolean, date, object, SchemaOf, string } from "yup";

const pafSignatureSchema: SchemaOf<IPafApplicationSignature> = object({
    is_attest: boolean().label(pafApplicationSignatureFields.isAttest.label)
        .required()
        .oneOf([true], "Please use the checkbox to indicate you have read and understand the terms of the USPS Postal Acknowledgement Form"),
    signature_text: string().label(pafApplicationSignatureFields.signature_text.label)
        .max(64).required("Your signature is required, please type your name on the signature line."),
    signature_date: date().label(pafApplicationSignatureFields.signedDate.label).optional().nullable(),
});

export const SignatureDialog: React.FC<ISignatureDialogProps> = (
    {
        isOpen = false,
        onClose,
        title = "e-Signature",
        terms = undefined,
        source,
    }: ISignatureDialogProps
) => {
    const theme = useTheme();
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    // defining the initial state for the form
    const initialValues = {
        is_attest: false,
        signature_text: '',
        signature_date: null,
    }

    const { control, handleSubmit, reset, setValue, formState: { errors, isValid } } = useForm<IPafApplicationSignature>({
        defaultValues: initialValues,
        resolver: yupResolver(pafSignatureSchema)
    });

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const handleClose = () => {
        if (onClose) {
            onClose(source!, "cancel");
        }
        reset(initialValues);
    };

    const onSubmit: SubmitHandler<IPafApplicationSignature> = (data) => {
        data.signature_date = new Date();
        if (onClose) {
            onClose(source!, "submit", data);
        }
        reset(initialValues);
    };

    const onSubmitErrors: SubmitErrorHandler<IPafApplicationSignature> = (errors) => {
        let errText: string = "";
        let key: keyof typeof errors;
        for (key in errors) {
            errText += `- ${errors[key]?.message}\n`;
        }
        msgBox_Show(
            `There is a problem with PAF Signature...\n${errText}`,
            "Invalid PAF Signature...",
            "error"
        );
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                scroll={"paper"}
                maxWidth={"md"}
                aria-labelledby="dialog-box-title"
                aria-describedby="dialog-box-description"
            >
                <DialogTitle
                    id="dialog-box-title"
                    sx={{
                        display: "flex",
                        alignItems: "end",
                        justifyContent: "start"
                    }}
                >
                    {title}
                </DialogTitle>
                <DialogContent dividers={true}>
                    {terms}
                </DialogContent>
                <DialogActions>
                    <Form
                        autoComplete="off"
                    >
                        <Grid container>
                            <Grid item xs={12} md={12}>
                                <DaCheckboxField
                                    control={control}
                                    name={pafApplicationSignatureFields.isAttest.name}
                                    label={pafApplicationSignatureFields.isAttest.label}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DaTextField
                                    control={control}
                                    name={pafApplicationSignatureFields.signature_text.name}
                                    label={pafApplicationSignatureFields.signature_text.label}
                                    required={pafApplicationSignatureFields.signature_text.required}
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <HTMLComment text={"Placeholder"} />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <ButtonBar buttonAlignment="alignRight">
                                    <DaButton buttonStyle={"secondary"} variant={"outlined"} autoFocus onClick={handleClose}>Cancel</DaButton>
                                    <DaButton buttonStyle={"primary"} variant={"outlined"} type="button"
                                        onClick={handleSubmit(onSubmit, onSubmitErrors)}>OK</DaButton>
                                </ButtonBar>
                            </Grid>
                        </Grid>
                    </Form>
                </DialogActions>
            </Dialog>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
        </>
    );
}